import { jsxs, jsx } from "react/jsx-runtime";
import assert from "assert";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { useRef, useReducer, useEffect, Children } from "react";
import { flattenChildren } from "../../utils/flattenChildren.mjs";
import { Box } from "../Box/Box.mjs";
import { useBraidTheme } from "../BraidProvider/BraidThemeContext.mjs";
import { MenuItemDivider } from "../MenuItemDivider/MenuItemDivider.mjs";
import { Overlay } from "../private/Overlay/Overlay.mjs";
import { Popover } from "../private/Popover/Popover.mjs";
import { ScrollContainer } from "../private/ScrollContainer/ScrollContainer.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { getNextIndex } from "../private/getNextIndex.mjs";
import { normalizeKey } from "../private/normalizeKey.mjs";
import { actionTypes } from "./MenuRenderer.actions.mjs";
import { MenuRendererContext } from "./MenuRendererContext.mjs";
import { MenuRendererItemContext } from "./MenuRendererItemContext.mjs";
import { menuYPadding, width, menuHeightLimit } from "./MenuRenderer.css.mjs";
import { vars } from "../../themes/vars.css.mjs";
const {
  MENU_TRIGGER_UP,
  MENU_ITEM_UP,
  MENU_TRIGGER_DOWN,
  MENU_ITEM_DOWN,
  MENU_ITEM_ENTER,
  MENU_ITEM_SPACE,
  MENU_ITEM_CLICK,
  MENU_ITEM_HOVER,
  MENU_TRIGGER_ENTER,
  MENU_TRIGGER_SPACE,
  MENU_TRIGGER_CLICK,
  MENU_CLOSE
} = actionTypes;
const CLOSED_INDEX = -1;
const CLOSE_REASON_EXIT = { reason: "exit" };
const initialState = {
  open: false,
  highlightIndex: CLOSED_INDEX,
  closeReason: CLOSE_REASON_EXIT
};
const MenuRenderer = ({
  onOpen,
  onClose,
  trigger,
  size = "standard",
  width: width2 = "content",
  align = "left",
  offsetSpace = "none",
  reserveIconSpace = false,
  placement = "bottom",
  children,
  data,
  ...restProps
}) => {
  const buttonRef = useRef(null);
  const lastOpen = useRef(false);
  const items = flattenChildren(children);
  const itemCount = items.filter((item) => !isDivider(item)).length;
  assert(
    items.every(
      (item) => typeof item === "object" && "type" in item && // @ts-expect-error
      item.type.__isMenuItem__
    ),
    "All child nodes within a menu component must be a MenuItem, MenuItemLink, MenuItemCheckbox or MenuItemDivider: https://seek-oss.github.io/braid-design-system/components/MenuRenderer"
  );
  const [{ open, highlightIndex, closeReason }, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case MENU_TRIGGER_UP:
        case MENU_ITEM_UP: {
          return {
            ...state,
            open: true,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: getNextIndex(-1, state.highlightIndex, itemCount)
          };
        }
        case MENU_TRIGGER_DOWN:
        case MENU_ITEM_DOWN: {
          return {
            ...state,
            open: true,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: getNextIndex(1, state.highlightIndex, itemCount)
          };
        }
        case MENU_CLOSE: {
          return {
            ...state,
            open: false,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: CLOSED_INDEX
          };
        }
        case MENU_ITEM_ENTER:
        case MENU_ITEM_SPACE:
        case MENU_ITEM_CLICK: {
          if ("formElement" in action && action.formElement) {
            return state;
          }
          return {
            ...state,
            open: false,
            closeReason: {
              reason: "selection",
              index: action.index,
              id: action.id
            },
            highlightIndex: CLOSED_INDEX
          };
        }
        case MENU_ITEM_HOVER: {
          return { ...state, highlightIndex: action.value };
        }
        case MENU_TRIGGER_ENTER:
        case MENU_TRIGGER_SPACE: {
          const nextOpen = !state.open;
          return {
            ...state,
            open: nextOpen,
            closeReason: CLOSE_REASON_EXIT,
            highlightIndex: nextOpen ? 0 : CLOSED_INDEX
          };
        }
        case MENU_TRIGGER_CLICK: {
          const nextOpen = !state.open;
          return {
            ...state,
            open: nextOpen,
            closeReason: CLOSE_REASON_EXIT
          };
        }
        default:
          return state;
      }
    },
    initialState
  );
  useEffect(() => {
    if (lastOpen.current === open) {
      return;
    }
    if (open && typeof onOpen === "function") {
      onOpen();
    } else if (!open && typeof onClose === "function") {
      onClose(closeReason);
    }
    lastOpen.current = open;
  }, [onOpen, onClose, open, closeReason]);
  const focusTrigger = () => {
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus();
    }
  };
  const onKeyDown = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === " " && /firefox|iceweasel|fxios/i.test(navigator.userAgent)) {
      return;
    }
    const isArrowPress = targetKey.indexOf("Arrow") === 0;
    const isActionKeyPress = targetKey === "Enter" || targetKey === " ";
    if (isArrowPress || isActionKeyPress) {
      event.preventDefault();
    }
    const action = {
      ArrowDown: { type: MENU_TRIGGER_DOWN },
      ArrowUp: { type: MENU_TRIGGER_UP },
      Enter: { type: MENU_TRIGGER_ENTER },
      " ": { type: MENU_TRIGGER_SPACE }
    };
    if (action[targetKey]) {
      dispatch(action[targetKey]);
    }
  };
  const triggerProps = {
    "aria-haspopup": true,
    "aria-expanded": open,
    role: "button",
    tabIndex: 0,
    ref: buttonRef,
    onKeyDown,
    onClick: (event) => {
      event.stopPropagation();
      event.preventDefault();
      dispatch({ type: MENU_TRIGGER_CLICK });
    }
  };
  return /* @__PURE__ */ jsxs(Box, { ...buildDataAttributes({ data, validateRestProps: restProps }), children: [
    trigger(triggerProps, { open }),
    /* @__PURE__ */ jsx(
      Popover,
      {
        open,
        onClose: () => dispatch({ type: MENU_CLOSE }),
        triggerRef: triggerProps.ref,
        align,
        placement,
        offsetSpace,
        role: false,
        children: /* @__PURE__ */ jsx(
          Menu,
          {
            align,
            size,
            width: width2,
            placement,
            highlightIndex,
            reserveIconSpace,
            focusTrigger,
            dispatch,
            children: items
          }
        )
      }
    )
  ] });
};
const isDivider = (node) => typeof node === "object" && node !== null && "type" in node && node.type === MenuItemDivider;
const borderRadius = "large";
function Menu({
  size,
  width: width$1,
  placement,
  children,
  dispatch,
  focusTrigger,
  highlightIndex,
  reserveIconSpace
}) {
  let dividerCount = 0;
  const menuYPadding$1 = useBraidTheme().legacy && size === "small" ? "xsmall" : "xxsmall";
  const inlineVars = assignInlineVars({
    [menuYPadding]: vars.space[menuYPadding$1]
  });
  return /* @__PURE__ */ jsx(MenuRendererContext.Provider, { value: { size, reserveIconSpace }, children: /* @__PURE__ */ jsxs(
    Box,
    {
      role: "menu",
      position: "relative",
      boxShadow: placement === "top" ? "small" : "medium",
      borderRadius,
      background: "surface",
      transition: "fast",
      overflow: "hidden",
      style: inlineVars,
      className: width$1 !== "content" && width[width$1],
      children: [
        /* @__PURE__ */ jsx(
          ScrollContainer,
          {
            direction: "vertical",
            fadeSize: size === "standard" ? "medium" : "small",
            children: /* @__PURE__ */ jsx(Box, { paddingY: menuYPadding$1, className: menuHeightLimit, children: Children.map(children, (item, i) => {
              if (isDivider(item)) {
                dividerCount++;
                return item;
              }
              const menuItemIndex = i - dividerCount;
              return /* @__PURE__ */ jsx(
                MenuRendererItemContext.Provider,
                {
                  value: {
                    isHighlighted: menuItemIndex === highlightIndex,
                    index: menuItemIndex,
                    dispatch,
                    focusTrigger
                  },
                  children: item
                },
                menuItemIndex
              );
            }) })
          }
        ),
        /* @__PURE__ */ jsx(
          Overlay,
          {
            boxShadow: "borderNeutralLight",
            borderRadius,
            visible: true
          }
        )
      ]
    }
  ) });
}
export {
  MenuRenderer
};
